import type { UserType } from '@kanbu/schema';
import { Role } from '@kanbu/schema/enums';
import { redirect } from '@tanstack/react-router';

import { useBoundStore } from '@/store/store';

import { ForbiddenError } from './errors';

/**
 * A user is "partner-capable" when they are a member of at least one partner
 * hub organization. Derived from the user's organizations (loaded via `me`), so
 * it works synchronously in route guards without an extra query.
 */
export function isPartnerCapable(user: UserType | null): boolean {
  return Boolean(
    user?.organizations?.some(org => org.isPartnerOrganization === true),
  );
}

/**
 * Guard that checks if the user is authorized to access the route.
 * If the user is not authorized, it throws a ForbiddenError.
 *
 * Non-SuperAdmins are normally restricted to their own current org. Partner
 * admins are exempt from that same-org check — they manage client orgs they
 * have not necessarily switched into (that's the point of the Organizations
 * list). This is only a client-side UX gate; the real boundary is server-side
 * (`organizations.findOne` / `assertCanManageClientOrganization` scope a
 * partner to their hub's actual portfolio regardless of what this guard lets
 * through).
 */
export function accessGuard(roles: Role[], organizationId?: string) {
  const { user, currentOrganizationId } = useBoundStore.getState();

  if (!user) {
    throw new ForbiddenError('User is not available.');
  }

  if (!roles.includes(user.role)) {
    throw new ForbiddenError('User is not authorized.');
  }

  if (
    user.role !== Role.SuperAdmin &&
    !isPartnerCapable(user) &&
    organizationId &&
    currentOrganizationId !== organizationId
  ) {
    throw new ForbiddenError('User is not authorized.');
  }
}

/**
 * Guard for pages available to SuperAdmin and partner-capable admins
 * (Organizations/Chats lists, partner billing). Throws ForbiddenError for
 * plain org admins.
 */
export function partnerOrSuperAdminGuard() {
  const { user } = useBoundStore.getState();

  if (!user) {
    throw new ForbiddenError('User is not available.');
  }

  if (user.role !== Role.SuperAdmin && !isPartnerCapable(user)) {
    throw new ForbiddenError('User is not authorized.');
  }
}

/**
 * Guard that checks if the user is authenticated.
 * If the user is not authenticated, it redirects to the login page.
 */
export function authGuard() {
  if (useBoundStore.getState().jwt === null) {
    throw redirect({
      to: '/login',
      search: {
        // Preserve path + query (e.g. /inbox?threadId=…) for post-login return
        redirect: `${location.pathname}${location.search}`,
      },
    });
  }
}
